import React, { createContext, useContext, useEffect, useState } from 'react';
import { IntlProvider }                                          from 'react-intl';

// language files
import de                from '../config/i18n/de.json';
import en                from '../config/i18n/en.json';
import { flattenObject } from "../utils";
import _                 from "lodash";

const DEFAULT_LANGUAGE = localStorage.getItem('@language') || window.navigator.language.replace(/^([\w]+)(-.*)/ig, '$1');

export interface TranslationsType<T = string> {
    [key: string]: {
        [key: string]: T
    }
}

export interface TranslationsContextType {
    translations: TranslationsType;
    currentLanguage: string;
    setLanguage: (key: string) => void;
    addTranslations: (translations: TranslationsType<any>) => void;
}

export const TranslationsContext = createContext<TranslationsContextType>({
    translations: {},
    currentLanguage: DEFAULT_LANGUAGE,
    setLanguage: (key: string) => {
    },
    addTranslations: (translations: TranslationsType<any>) => {
    },
});

export type TranslationState = {
    translations: TranslationsType;
    currentLanguage: string;
}

export const useCreateTranslations = (initialLanguage: string, initialTranslations: TranslationsType) => {
    const [{ translations, currentLanguage }, updateTranslationState] = useState<TranslationState>({
        translations: initialTranslations,
        currentLanguage: initialLanguage
    });

    const addTranslations = (translations: TranslationsType<any>) => {
        const flattenedLang = Object.keys(translations).reduce<TranslationsType>((stack, lang: string) => {
            stack[lang] = flattenObject(translations[lang]);
            return stack;
        }, {});
        updateTranslationState({
            translations: _.merge(translations, flattenedLang),
            currentLanguage
        })
    };

    const setLanguage = (lang: string) => {
        updateTranslationState({
            translations,
            currentLanguage: lang
        });
        localStorage.setItem('@language', lang);
    }

    useEffect(() => {
        addTranslations(initialTranslations);
    }, [initialTranslations]);

    return {
        translations,
        currentLanguage,
        setLanguage,
        addTranslations
    }
}

export const useTranslations = (): TranslationsContextType => {
    return useContext(TranslationsContext);
}

const defaultTranslations: TranslationsType = {
    de: flattenObject(de),
    en: flattenObject(en),
};

type LanguageProviderProps = {
    initialLanguage?: string;
    initialTranslations?: TranslationsType;
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ initialLanguage, initialTranslations, children }) => {
    const defaultLocale = initialLanguage || (defaultTranslations[DEFAULT_LANGUAGE] ? DEFAULT_LANGUAGE : 'en');
    const translations = useCreateTranslations(
        defaultLocale,
        _.merge(defaultTranslations, initialTranslations));

    const { setLanguage } = useTranslations();

    useEffect(() => {
        if (initialLanguage) {
            setLanguage(defaultLocale);
        }
    }, [defaultLocale]);

    const { currentLanguage, translations: currentTranslations } = translations;

    const onIntlError = () => {

    }

    return (
        <TranslationsContext.Provider value={ translations }>
            <IntlProvider
                defaultLocale={ 'en' }
                locale={ currentLanguage }
                messages={ currentTranslations[currentLanguage] }
                onError={ onIntlError }
            >
                { children }
            </IntlProvider>
        </TranslationsContext.Provider>
    );
};
